/**
 * Define the provider-neutral connector contracts rendered by Vault UI.
 *
 * Feed identity, direction, schedule, and job mutation shape are shared across
 * providers. Provider configuration stays in its provider folder; the feed's
 * provider-specific option payloads mirror the backend wire contract exactly.
 */
import type { KualiResearchFeedOptions } from './kuali-research/types'
import type { ServiceNowFeedOptions } from './servicenow/types'
import type { WorkdayFeedOptions } from './workday/types'
import type { LucideIcon } from 'lucide-react'

export type ConnectorCredentialSource = 'workspace' | 'umd'
export type ConnectorProvider = 'box' | 'kuali-research' | 'servicenow' | 'workday'
export type ConnectorDirection = 'inbound' | 'outbound'
export type ConnectorInitialSync = 'all' | 'future'
export type ConnectorRunMode = 'baseline' | 'estimate' | 'sync'
export type ConnectorDocumentDisposition = 'detach' | 'delete'
export type ConnectorAnalysisAction = 'reset'
export type ConnectorPendingAction = 'pull' | 'remove' | 'sync' | 'reset'
export type ConnectorAnalysisState =
  | 'empty'
  | 'not-started'
  | 'syncing'
  | 'running'
  | 'disconnecting'
  | 'paused'
  | 'complete'
  | 'stalled'
export type ConnectorSchedule =
  | 'manual'
  | 'every-minute'
  | 'every-5-minutes'
  | 'every-30-minutes'
  | 'hourly'
  | 'every-6-hours'
  | 'daily'

export interface ConnectorSyncWarning {
  externalId: string
  externalIdLabel: string
  message: string
}

export interface ConnectorSourceDisplayFieldDefinition {
  key: string
  label: string
  default: boolean
}

export interface ConnectorDocumentMappingRecordType {
  recordType: string
  artifactTypes: ('attachment' | 'metadata')[]
}

export interface ConnectorDocumentMappingSource {
  provider: ConnectorProvider
  recordTypes: ConnectorDocumentMappingRecordType[]
}

export interface ConnectorDocumentMappingCatalog {
  sources: ConnectorDocumentMappingSource[]
}

export interface ConnectorPullSource {
  feedId: string
  provider: ConnectorProvider
  sourceName: string
  recordUnit: { singular: string; plural: string }
  recordTypes: { recordType: string; label: string }[]
  identifierLabel: string
  resultLabel: string
  estimatedResultsPerIdentifierPerType: number
}

export interface ConnectorPullCatalog {
  sources: ConnectorPullSource[]
}

export interface ConnectorManagedRecord {
  recordKey: string
  identifier: string
  title?: string | null
  status: 'synced' | 'syncing'
}

interface ConnectorFeedBase {
  feedId: string
  vaultId: string
  direction: 'inbound'
  remoteFolderId: string
  remoteFolderName: string
  groupId?: string
  createdAt: string
  createdBy: string
  lastSyncedAt?: string
  analysisPausedAt?: string
  disconnectJobId?: string
  disconnectStartedAt?: string
  disconnectDisposition?: ConnectorDocumentDisposition
  lastSyncWarnings: ConnectorSyncWarning[]
  sourceDisplayFieldKeys: string[]
  schedule: ConnectorSchedule
}

export interface BoxConnectorFeed extends ConnectorFeedBase {
  provider: 'box'
  oauthUserId: string
  box: {
    folders: Array<{ id: string; name: string }>
  }
}

export interface KualiResearchConnectorFeed extends ConnectorFeedBase {
  provider: 'kuali-research'
  oauthUserId?: null
  kualiResearch: KualiResearchFeedOptions
}

export interface ServiceNowConnectorFeed extends ConnectorFeedBase {
  provider: 'servicenow'
  oauthUserId?: null
  servicenow: ServiceNowFeedOptions
}

export interface WorkdayConnectorFeed extends ConnectorFeedBase {
  provider: 'workday'
  oauthUserId?: null
  workday: WorkdayFeedOptions
}

// cm-coding: Each connector editor must receive settings for the feed's declared
// provider. Use provider as the discriminant for API responses and UI state so
// TypeScript rejects missing or mismatched settings before an editor can save
// them as another connector type.
export type ConnectorFeed =
  | BoxConnectorFeed
  | KualiResearchConnectorFeed
  | ServiceNowConnectorFeed
  | WorkdayConnectorFeed

export type ConnectorFeedFor<Provider extends ConnectorProvider> = Extract<
  ConnectorFeed,
  { provider: Provider }
>

export interface ConnectorFeedMutation {
  feed: ConnectorFeed
  jobId?: string
  status: string
}

export interface ConnectorSyncRun {
  vaultId: string
  feedId: string
  jobId: string
  sourceRecords: number
  documentCount: number
  completedAt: string
}

export interface ConnectorSyncFailure {
  jobId: string
  message: string
  failedAt: string
}

export interface ConnectorAnalysisStatus {
  feedId: string
  provider: ConnectorProvider
  sourceName: string
  state: ConnectorAnalysisState
  paused: boolean
  sourceRecords: number
  sourceRecordUnit: {
    singular: string
    plural: string
  }
  connectorDocuments: number
  attachmentDocuments: number
  metadataDocuments: number
  eligibleRecords: number
  analyzedRecords: number
  remainingRecords: number
  automaticSyncEnabled: boolean
  firstCompletedAt?: string
  lastCompletedAt?: string
  schedule: ConnectorSchedule
  syncRunCount: number
  syncRuns: ConnectorSyncRun[]
  lastSyncFailure: ConnectorSyncFailure | null
  destinationVaultName: string
  destinationFolderName: string
  configurationSummary: string[]
}

export interface ConnectorAnalysisStatusList {
  analyses: ConnectorAnalysisStatus[]
}

export interface ConnectorAnalysisActionResponse {
  feedId: string
  action: ConnectorAnalysisAction
  jobIds: string[]
}

export interface ConnectorSelectionPreview {
  itemCount: number
  itemLabel: string
  // cm-coding: Metadata-only connector previews deliberately defer document
  // discovery to the jobs worker because some provider "metadata" embeds full
  // file bodies. Null is the API's explicit signal that the safe web request
  // verified the record scope without loading attachments into web memory.
  documentCount?: number | null
  runMode: ConnectorRunMode
}

export const sameConnectorSelection = (
  left: ConnectorSelectionPreview | undefined,
  right: ConnectorSelectionPreview,
) => Boolean(
  left
  && left.itemCount === right.itemCount
  && left.itemLabel === right.itemLabel
  && left.documentCount === right.documentCount
  && left.runMode === right.runMode,
)

export interface ConnectorPickerAction {
  id: string
  provider: ConnectorProvider
  label: string
  description: string
  Icon: LucideIcon
  connectedCount: number
  disabled?: boolean
  // Shown in place of the description when disabled, so a workspace admin
  // learns why a provider is unavailable instead of seeing a dead row.
  disabledReason?: string
  onSelect: () => void
}
